// ═══════════════════════════════════════════════════════════════════════════════
// Pair Code Invite Landing (public) — /pair-code/:code
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  PAIRING_CODE_LENGTH,
  PAIRING_CODE_PREVIEW_REASON,
  USER_ROLE,
  type PairingCodePreviewResponse,
} from '@fdh/shared';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { AuthLandingNavButtons } from '../../components/auth/AuthLandingNavButtons';
import { AppButton, appButtonClassName } from '../../components/ui/AppButton';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { AuthLayout } from '../auth/AuthLayout';
import { api } from '../../lib/api';
import { executePairInviteJoin } from '../../lib/completePendingPairInvite';
import { startGoogleRedirectAuth } from '../../lib/googleIdentity';
import { writePendingPairInvite } from '../../lib/pairInviteSession';

const normalizeRouteCode = (raw: string | undefined): string =>
  (raw ?? '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, PAIRING_CODE_LENGTH);

const isPairedProfile = (pairedAt: string | undefined): boolean =>
  Boolean(pairedAt && pairedAt.trim() !== '');

export const PairCodeInvitePage = () => {
  const { code: routeCode } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { isAuthenticated, user, isLoading: authLoading, refreshUser } = useAuth();

  const code = normalizeRouteCode(routeCode);

  const [preview, setPreview] = useState<PairingCodePreviewResponse | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [previewError, setPreviewError] = useState('');
  const [actionError, setActionError] = useState('');
  const [googleBusy, setGoogleBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [joinBusy, setJoinBusy] = useState(false);

  const previewInvalidMessage = useCallback(
    (reason: string | undefined): string => {
      if (reason === PAIRING_CODE_PREVIEW_REASON.FORMAT_INVALID) {
        return t('pairing.invite.errorFormat');
      }
      return t('pairing.invite.errorInvalid');
    },
    [t]
  );

  useEffect(() => {
    if (code.length !== PAIRING_CODE_LENGTH) {
      setPreview({ valid: false, reason: PAIRING_CODE_PREVIEW_REASON.FORMAT_INVALID });
      setPreviewLoading(false);
      return;
    }

    const run = async () => {
      setPreviewLoading(true);
      setPreviewError('');
      try {
        const response = await api.post<PairingCodePreviewResponse>('/pairing/code-preview', {
          code,
        });
        if (!response.success || !response.data) {
          setPreview({ valid: false, reason: PAIRING_CODE_PREVIEW_REASON.NOT_FOUND });
          return;
        }
        setPreview(response.data);
      } catch {
        setPreviewError(t('pairing.invite.previewError'));
      } finally {
        setPreviewLoading(false);
      }
    };
    void run();
  }, [code, t]);

  const stashInvite = useCallback(() => {
    if (!preview?.valid || !preview.employerNickname) {
      return;
    }
    writePendingPairInvite({
      code,
      employerNickname: preview.employerNickname,
    });
  }, [code, preview]);

  const handleEmailLogin = useCallback(() => {
    stashInvite();
    navigate('/login');
  }, [navigate, stashInvite]);

  const handleRegister = useCallback(() => {
    stashInvite();
    navigate('/register');
  }, [navigate, stashInvite]);

  const handleGoogleLogin = useCallback(() => {
    setActionError('');
    stashInvite();
    setGoogleBusy(true);
    try {
      startGoogleRedirectAuth({
        intent: 'login',
        pairInviteMode: true,
        pairingCode: code,
        role: 'helper',
        fromPath: `/pair-code/${code}`,
      });
    } catch (err) {
      setGoogleBusy(false);
      setActionError(err instanceof Error ? err.message : t('auth.errorGeneric'));
    }
  }, [code, stashInvite, t]);

  const handleOpenConfirmJoin = useCallback(() => {
    setConfirmOpen(true);
  }, []);

  const handleCloseConfirmJoin = useCallback(() => {
    if (joinBusy) {
      return;
    }
    setConfirmOpen(false);
  }, [joinBusy]);

  const handleConfirmJoin = useCallback(async () => {
    if (!preview?.employerNickname) {
      return;
    }
    setJoinBusy(true);
    setActionError('');
    writePendingPairInvite({
      code,
      employerNickname: preview.employerNickname,
    });
    try {
      await executePairInviteJoin(refreshUser, navigate);
      setConfirmOpen(false);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : t('pairing.join.errorGeneric'));
    } finally {
      setJoinBusy(false);
    }
  }, [code, navigate, preview?.employerNickname, refreshUser, t]);

  if (previewLoading || authLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-950"
        role="status"
        aria-label={t('common.loading')}
      >
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  if (previewError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-950">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-gray-900">
          <p className="text-sm text-red-700 dark:text-red-300" role="alert">
            {previewError}
          </p>
          <Link
            to="/"
            className="mt-6 inline-block text-sm font-semibold text-primary-600 underline dark:text-primary-400"
          >
            {t('auth.goToHome')}
          </Link>
        </div>
      </div>
    );
  }

  if (!preview?.valid) {
    return (
      <AuthLayout>
        <h1 className="text-center text-xl font-bold text-gray-900" id="pair-invite-invalid-heading">
          {t('pairing.invite.invalidTitle')}
        </h1>
        <p className="mt-3 text-center text-sm text-gray-600" role="alert">
          {previewInvalidMessage(preview?.reason)}
        </p>
        <Link
          to="/"
          className={appButtonClassName({ variant: 'primary', fullWidth: true, className: 'mt-6' })}
          aria-labelledby="pair-invite-invalid-heading"
        >
          {t('auth.goToHome')}
        </Link>
      </AuthLayout>
    );
  }

  const employerNickname = preview.employerNickname ?? '';

  if (isAuthenticated && user) {
    if (user.role === USER_ROLE.EMPLOYER) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-950">
          <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-gray-900">
            <p className="text-sm text-amber-800 dark:text-amber-200">{t('pairing.invite.employerSignedIn')}</p>
            <Link
              to="/pairing/status"
              className="mt-6 inline-block text-sm font-semibold text-primary-600 underline dark:text-primary-400"
            >
              {t('pairing.invite.goPairingStatus')}
            </Link>
          </div>
        </div>
      );
    }

    if (user.role === USER_ROLE.HELPER && isPairedProfile(user.pairedAt)) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-950">
          <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-gray-900">
            <p className="text-sm text-gray-700 dark:text-gray-200">{t('pairing.invite.helperAlreadyPaired')}</p>
            <Link
              to="/pairing/status"
              className="mt-6 inline-block text-sm font-semibold text-primary-600 underline dark:text-primary-400"
            >
              {t('pairing.invite.goPairingStatus')}
            </Link>
          </div>
        </div>
      );
    }

    if (user.role === USER_ROLE.HELPER) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-950">
          <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-lg dark:bg-gray-900">
            <h1 className="text-center text-xl font-bold text-gray-900 dark:text-white">
              {t('pairing.invite.confirmTitle', { nickname: employerNickname })}
            </h1>
            <p className="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">
              {t('pairing.invite.confirmSubtitle')}
            </p>
            {actionError ? (
              <p className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300" role="alert">
                {actionError}
              </p>
            ) : null}
            <AppButton
              type="button"
              variant="primary"
              size="lg"
              fullWidth
              className="mt-6"
              onClick={handleOpenConfirmJoin}
              disabled={joinBusy}
            >
              {t('pairing.invite.confirmCta')}
            </AppButton>
          </div>
          <ConfirmDialog
            open={confirmOpen}
            title={t('pairing.invite.confirmDialogTitle', { nickname: employerNickname })}
            description={t('pairing.invite.confirmDialogDescription')}
            confirmLabel={joinBusy ? t('pairing.join.pairing') : t('common.confirm')}
            cancelLabel={t('common.cancel')}
            onConfirm={() => void handleConfirmJoin()}
            onCancel={handleCloseConfirmJoin}
          />
        </div>
      );
    }
  }

  return (
    <AuthLayout>
      <h1
        className="text-center text-xl font-bold text-gray-900"
        id="pair-invite-heading"
      >
        {t('pairing.invite.landingTitle', { nickname: employerNickname })}
      </h1>
      <p className="mt-2 text-center text-sm text-gray-500">
        {t('pairing.invite.landingSubtitle')}
      </p>

      {actionError ? (
        <p
          className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700"
          role="alert"
        >
          {actionError}
        </p>
      ) : null}

      <AuthLandingNavButtons
        className="mt-8 space-y-4"
        onEmailLogin={handleEmailLogin}
        onGoogleLogin={handleGoogleLogin}
        onRegister={handleRegister}
        googleBusy={googleBusy}
      />
    </AuthLayout>
  );
};
